import React, { createContext, useContext, useEffect, useState } from "react";
import { useMantineColorScheme, useComputedColorScheme } from "@mantine/core";
import { useAuth } from "./auth";

type Theme = "dark" | "light" | "auto";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "dark" | "light";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("theme") as Theme) || "auto";
    }
    return "auto";
  });
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  // Try to access auth context, but don't throw if it's not available
  let user: any;
  try {
    const auth = useAuth();
    user = auth?.user;
  } catch {
    // Auth context might not be available yet
  }

  // Initialize theme from user preference
  useEffect(() => {
    if (user?.profile?.theme) {
      setThemeState(user.profile.theme as Theme);
    }
  }, [user]);

  // Sync theme choice to Mantine
  useEffect(() => {
    localStorage.setItem("theme", theme);
    setColorScheme(theme);
  }, [theme, setColorScheme]);

  const resolvedTheme = useComputedColorScheme("light");

  const setTheme = (t: Theme) => setThemeState(t);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
